import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import Layout from '../../components/Layout/Layout';
import { MdSmartToy, MdSend, MdAdd, MdContentCopy, MdClose, MdCode, MdFileDownload } from '../../utils/icons';
import { apiRequest } from '../../auth/msalConfig';
import { fetchWithRetry, TRANSIENT_STATUS, TRANSIENT_MESSAGE } from '../../utils/http';
import './ProductAgent.css';

// ── Types ────────────────────────────────────────────────────────────────────

interface Message {
  id: string;
  role: 'user' | 'agent';
  text: string;
  timestamp: string;
  jsonData?: unknown;
  hints?: string[];
}

interface AgentResponse {
  text: string;
  jsonData?: unknown;
  hints?: string[];
}

// ── Emoji shortcode resolver ─────────────────────────────────────────────────

const EMOJI_MAP: Record<string, string> = {
  ':white_check_mark:': '✅',
  ':x:': '❌',
  ':warning:': '⚠️',
  ':arrows_counterclockwise:': '🔄',
  ':wave:': '👋',
  ':blush:': '😊',
  ':raised_hands:': '🙌',
  ':robot_face:': '🤖',
  ':mag:': '🔍',
  ':clipboard:': '📋',
  ':bell:': '🔔',
  ':information_source:': 'ℹ️',
  ':check:': '✔️',
  ':tada:': '🎉',
  ':fire:': '🔥',
  ':hourglass:': '⏳',
};

const resolveEmojis = (text: string): string =>
  Object.entries(EMOJI_MAP).reduce((s, [code, emoji]) => s.replaceAll(code, emoji), text);

// ── Inline markdown (bold/italic) ────────────────────────────────────────────

const renderInline = (text: string): React.ReactNode[] => {
  const segments = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return segments.map((seg, i) => {
    if (seg.startsWith('**') && seg.endsWith('**'))
      return <strong key={i}>{seg.slice(2, -2)}</strong>;
    if (seg.startsWith('*') && seg.endsWith('*'))
      return <em key={i}>{seg.slice(1, -1)}</em>;
    return <React.Fragment key={i}>{seg}</React.Fragment>;
  });
};

// ── Product JSON key transformer ─────────────────────────────────────────────

const PRODUCT_KEY_MAP: Record<string, string> = {
  primary_id: 'ProductNumber',
  company_code: 'CompanyCode',
  found_product_in_ax_logs: 'IsProductEntryFoundInEDILogs',
  found_product_in_ax: 'IsProductExistInAX',
  error: 'ErrorMessage',
  message_status: 'EDIStatus',
  product_log_time: 'EDILogTime',
  product_modified_date_time_in_ax: 'LastModifiedDateTimeOfProductInAx',
  product_created_date_time_in_ax: 'CreatedDateTimeOfProductInAx',
  product_modified_by_in_ax: 'LastModifiedByOfProductInAx',
  product_created_by_in_ax: 'CreatedByOfProductInAx',
};

// Keys whose values should be converted from UTC to CET/CEST (checked before key rename).
const PRODUCT_DATE_KEYS = new Set([
  'product_log_time',
  'product_modified_date_time_in_ax',
  'product_created_date_time_in_ax',
]);

// Parse "M/D/YYYY h:mm:ss AM/PM" as a UTC instant.
const parseUtcDateString = (s: string): Date | null => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s+(AM|PM)$/i.exec(s.trim());
  if (!m) return null;
  const [, mon, day, year, rawH, min, sec, ampm] = m;
  let h = parseInt(rawH, 10);
  if (ampm.toUpperCase() === 'AM') { if (h === 12) h = 0; }
  else { if (h !== 12) h += 12; }
  const d = new Date(Date.UTC(+year, +mon - 1, +day, h, +min, +sec));
  return isNaN(d.getTime()) ? null : d;
};

// Convert a UTC date string to a CET/CEST formatted string.
// Returns the original value unchanged for null/undefined/empty/unrecognised strings.
const convertUtcToCet = (value: unknown): unknown => {
  if (value === null || value === undefined || value === '') return value;
  if (typeof value !== 'string') return value;
  const date = parseUtcDateString(value);
  if (!date) return value;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).formatToParts(date);
  const p: Record<string, string> = {};
  parts.forEach(({ type, value: v }) => { p[type] = v; });
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}:${p.second} ${p.timeZoneName}`;
};

const transformProductJson = (data: unknown): unknown => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  const src = data as Record<string, unknown>;
  const hasProductKey = Object.keys(src).some(k => k in PRODUCT_KEY_MAP);
  if (!hasProductKey) return data;
  return Object.fromEntries(
    Object.entries(src).map(([k, v]) => [
      PRODUCT_KEY_MAP[k] ?? k,
      PRODUCT_DATE_KEYS.has(k) ? convertUtcToCet(v) : v,
    ]),
  );
};

// ── HTML detection & splitting ───────────────────────────────────────────────

const HTML_TAG_RE = /<[a-zA-Z][^>]*>/;

const containsHtml = (text: string): boolean => HTML_TAG_RE.test(text);

// Split "Some prefix text <h3>HTML content..." into { prefix, html }
const splitAtHtml = (line: string): { prefix: string; html: string } => {
  const idx = line.search(/<[a-zA-Z]/);
  if (idx === -1) return { prefix: line, html: '' };
  return { prefix: line.slice(0, idx), html: line.slice(idx) };
};

// Force agent links to open in a new tab so clicking a source doesn't navigate
// away and lose the conversation. Also hardens against reverse-tabnabbing.
// Adds target/rel only when the anchor doesn't already have them.
const openLinksInNewTab = (html: string): string =>
  html.replace(/<a\b([^>]*)>/gi, (_m, attrs: string) => {
    let a = attrs;
    if (!/\btarget=/i.test(a)) a += ' target="_blank"';
    if (!/\brel=/i.test(a)) a += ' rel="noopener noreferrer"';
    return `<a${a}>`;
  });

// ── Line classifier ───────────────────────────────────────────────────────────

type LineClass =
  | 'processing'
  | 'status-success'
  | 'status-error'
  | 'status-warning'
  | 'status-pending'
  | 'divider'
  | 'spacer'
  | 'normal';

const classifyLine = (line: string): LineClass => {
  const t = line.trim();
  if (t === '---') return 'divider';
  if (t === '') return 'spacer';
  if (line.startsWith('->')) return 'processing';
  if (line.startsWith('✅')) return 'status-success';
  if (line.startsWith('❌')) return 'status-error';
  if (line.startsWith('⚠️')) return 'status-warning';
  if (line.startsWith('🔄')) return 'status-pending';
  return 'normal';
};

// ── Agent message renderer ───────────────────────────────────────────────────

const AgentMessage: React.FC<{ text: string }> = ({ text }) => {
  const lines = resolveEmojis(text).split('\n');

  return (
    <div className="pa-agent-body">
      {lines.map((line, i) => {
        const cls = classifyLine(line);

        if (cls === 'divider') return <hr key={i} className="pa-divider" />;
        if (cls === 'spacer') return <div key={i} className="pa-spacer" />;

        // Line contains HTML — render the HTML portion properly.
        // Content comes from the AI agent (not user input) so dangerouslySetInnerHTML is safe here.
        if (containsHtml(line)) {
          const { prefix, html } = splitAtHtml(line);
          return (
            <div key={i} className={`pa-line pa-line--${cls}`}>
              {prefix && <span>{renderInline(prefix)}</span>}
              <div
                className="pa-html-block"
                dangerouslySetInnerHTML={{ __html: openLinksInNewTab(html) }}
              />
            </div>
          );
        }

        return (
          <div key={i} className={`pa-line pa-line--${cls}`}>
            {renderInline(line)}
          </div>
        );
      })}
    </div>
  );
};

// ── Suggested prompts ────────────────────────────────────────────────────────

const SUGGESTIONS = [
  'How do I create a new product in the PIM User Portal?',
  "Why isn't product P10001 showing up for BRI?",
  'Has product P10023 update been processed for Multiline (MLN) yet?',
  'Have the digital assets for product P10045 been processed by MFT for Global Net (GLN)?',
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatTime = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const newUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const extractAgentResponse = async (response: Response): Promise<AgentResponse> => {
  // Read the body once as text, then try to parse. Calling response.json() and
  // then response.text() on the same Response throws "body stream already read".
  const raw = await response.text();
  let body: unknown;
  try { body = JSON.parse(raw); } catch { return { text: raw }; }

  const pickJson = (obj: Record<string, unknown>): unknown | undefined => {
    const j = obj['json'];
    return j !== undefined && j !== null ? transformProductJson(j) : undefined;
  };

  const pickHints = (obj: Record<string, unknown>): string[] | undefined => {
    const h = obj['hints'];
    return Array.isArray(h) && h.length > 0 ? (h as string[]) : undefined;
  };

  if (Array.isArray(body) && body.length > 0) {
    const first = body[0];
    if (typeof first === 'string') return { text: first };
    if (first && typeof first === 'object') {
      const b0 = first as Record<string, unknown>;
      const jsonData = pickJson(b0);
      const hints = pickHints(b0);
      let text = JSON.stringify(first, null, 2);
      for (const key of ['output', 'text', 'message', 'response', 'result'])
        if (b0[key] !== undefined) { text = String(b0[key]); break; }
      return { text, ...(jsonData !== undefined && { jsonData }), ...(hints && { hints }) };
    }
    return { text: JSON.stringify(body[0], null, 2) };
  }

  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    const jsonData = pickJson(b);
    const hints = pickHints(b);
    let text = JSON.stringify(body, null, 2);
    for (const key of ['output', 'text', 'message', 'response', 'result'])
      if (b[key] !== undefined) { text = String(b[key]); break; }
    return { text, ...(jsonData !== undefined && { jsonData }), ...(hints && { hints }) };
  }

  return { text: JSON.stringify(body, null, 2) };
};

// ── JSON syntax highlighter ──────────────────────────────────────────────────

const highlightJson = (json: string): React.ReactNode[] => {
  const TOKEN_RE = /("(?:\\u[0-9a-fA-F]{4}|\\[^u]|[^"\\])*")(\s*:)?|true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|[{}[\],]/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0, key = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN_RE.exec(json)) !== null) {
    if (match.index > lastIndex) nodes.push(json.slice(lastIndex, match.index));
    const token = match[0];
    const isKey = match[2] !== undefined;
    let cls = 'pa-json-punctuation';
    if (token.startsWith('"')) cls = isKey ? 'pa-json-key' : 'pa-json-string';
    else if (token === 'true' || token === 'false') cls = 'pa-json-boolean';
    else if (token === 'null') cls = 'pa-json-null';
    else if (/^-?\d/.test(token)) cls = 'pa-json-number';
    nodes.push(<span key={key++} className={cls}>{token}</span>);
    lastIndex = TOKEN_RE.lastIndex;
  }
  if (lastIndex < json.length) nodes.push(json.slice(lastIndex));
  return nodes;
};

// ── JSON viewer modal ────────────────────────────────────────────────────────

interface JsonViewerModalProps {
  data: unknown;
  onClose: () => void;
  onDownload: () => void;
}

const JsonViewerModal: React.FC<JsonViewerModalProps> = ({ data, onClose, onDownload }) => {
  const formatted = JSON.stringify(data, null, 2);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div
      className="pa-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="JSON Viewer"
    >
      <div className="pa-modal">
        <div className="pa-modal-header">
          <div className="pa-modal-title">
            <MdCode aria-hidden="true" />
            <span>JSON Data</span>
          </div>
          <div className="pa-modal-actions">
            <button
              className="pa-modal-download-btn"
              onClick={onDownload}
              title="Download JSON file"
              aria-label="Download JSON"
            >
              <MdFileDownload aria-hidden="true" />
              Download
            </button>
            <button
              className="pa-modal-close-btn"
              onClick={onClose}
              title="Close (Esc)"
              aria-label="Close JSON viewer"
            >
              <MdClose aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="pa-modal-body">
          <pre className="pa-json-pre"><code>{highlightJson(formatted)}</code></pre>
        </div>
      </div>
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────────────

const ProductAgent: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string>(newUUID);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [viewingJson, setViewingJson] = useState<{ messageId: string; data: unknown } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const resetTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.value = '';
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleNewConversation = useCallback(() => {
    // Cancel any in-flight request so its response can't land in the new chat.
    abortRef.current?.abort();
    abortRef.current = null;
    setIsLoading(false);
    setMessages([]);
    setInputValue('');
    setConversationId(newUUID());
    resetTextarea();
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, []);

  const closeJsonModal = useCallback(() => setViewingJson(null), []);

  const handleJsonDownload = useCallback(() => {
    if (!viewingJson) return;
    const blob = new Blob([JSON.stringify(viewingJson.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-data-${viewingJson.messageId.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [viewingJson]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading) return;

      const msgId = newUUID();
      setMessages(prev => [
        ...prev,
        { id: msgId, role: 'user', text, timestamp: formatTime(new Date()) },
      ]);
      setInputValue('');
      setIsLoading(true);
      resetTextarea();

      const controller = new AbortController();
      abortRef.current = controller;
      const { signal } = controller;

      try {
        const url = process.env.REACT_APP_PRODUCT_AGENT_WEBHOOK_URL;
        if (!url) throw new Error('CONFIG_MISSING');

        let accessToken: string;
        try {
          const tokenResponse = await instance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          });
          accessToken = tokenResponse.accessToken;
        } catch {
          await instance.acquireTokenRedirect(apiRequest);
          return;
        }

        const res = await fetchWithRetry(() => fetch(url + '/webhook/product_chat_v2', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'x-ms-conversationid': conversationId,
          },
          body: JSON.stringify({ request_payload: text }),
          signal,
        }), { signal });

        // A transient 5xx that survived retries — flagged so the catch shows the
        // "starting up" copy instead of leaking HTTP_503 to the user.
        if (TRANSIENT_STATUS.has(res.status)) throw new Error('TRANSIENT');
        if (!res.ok) throw new Error(`HTTP_${res.status}`);

        const { text: agentText, jsonData, hints } = await extractAgentResponse(res);
        if (signal.aborted) return;
        setMessages(prev => [
          ...prev,
          {
            id: newUUID(),
            role: 'agent',
            text: agentText || 'No response received.',
            timestamp: formatTime(new Date()),
            ...(jsonData !== undefined && { jsonData }),
            ...(hints && { hints }),
          },
        ]);
      } catch (err: any) {
        if (signal.aborted) return; // chat was reset; drop the stale result/error
        const msg = err?.message ?? '';
        const friendly =
          msg === 'CONFIG_MISSING'
            ? 'Configuration Error: The Product Support AI Agent URL is not configured. Please check environment settings.'
            : msg === 'TRANSIENT'
              ? TRANSIENT_MESSAGE
              : `Connection Error: Unable to reach the Product Support AI Agent. (${msg})`;
        setMessages(prev => [
          ...prev,
          { id: newUUID(), role: 'agent', text: friendly, timestamp: formatTime(new Date()) },
        ]);
      } finally {
        // Skip if aborted: the new chat owns isLoading now.
        if (!signal.aborted) {
          flushSync(() => setIsLoading(false));
          textareaRef.current?.focus();
        }
      }
    },
    [conversationId, isLoading, accounts, instance],
  );

  // ID of the most recent agent message that carries hints — only those chips are active
  const lastHintsMsgId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === 'agent' && m.hints && m.hints.length > 0) return m.id;
    }
    return null;
  }, [messages]);

  const handleSend = () => sendMessage(inputValue);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    e.target.style.height = '1px';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  const copyMessage = (id: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  return (
    <Layout title="Product Support AI Agent">
      <div className="pa-wrap">

        {/* ── Header ── */}
        <div className="pa-header">
          <div className="pa-header-left">
            <div className="pa-header-icon">
              <MdSmartToy aria-hidden="true" />
            </div>
            <div>
              {/* <p className="pa-header-title">OmniLinQ Product Agent</p> */}
              <p className="pa-header-sub">
                Check product status · Investigate issues · Raise support requests
              </p>
            </div>
          </div>
          <div className="pa-header-right">
            <span className="pa-session-badge" title={`Full session ID: ${conversationId}`}>
              Session&nbsp;{conversationId.slice(0, 8)}
            </span>
            <button
              className="pa-new-btn"
              onClick={handleNewConversation}
              title="Start a new conversation (clears current chat)"
            >
              <MdAdd aria-hidden="true" />
              New Chat
            </button>
          </div>
        </div>

        {/* ── Chat area ── */}
        <div className="pa-chat">
          {messages.length === 0 ? (

            /* Welcome state */
            <div className="pa-welcome">
              <div className="pa-welcome-icon">
                <MdSmartToy aria-hidden="true" />
              </div>
              <h2 className="pa-welcome-title">OmniLinQ Product Support AI Agent</h2>
              <p className="pa-welcome-desc">
                Hi! I'm your Product Support Agent. I can help you with investigating
                product update integration issues between STEP and iHub, AX, and WMS;
                creating a TopDesk ticket for any issues you run into; and guiding you
                with user guides and FAQs for the STEP system.
              </p>
              <div className="pa-suggestions">
                {SUGGESTIONS.map(prompt => (
                  <button
                    key={prompt}
                    className="pa-suggestion"
                    onClick={() => {
                      setInputValue(prompt);
                      setTimeout(() => {
                        const ta = textareaRef.current;
                        if (ta) {
                          ta.focus();
                          ta.style.height = '1px';
                          ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
                        }
                      }, 0);
                    }}
                    disabled={isLoading}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>

          ) : (

            /* Message list */
            <div className="pa-messages">
              {messages.map(msg => (
                <div key={msg.id} className={`pa-row pa-row--${msg.role}`}>
                  {msg.role === 'agent' && (
                    <div className="pa-avatar">
                      <MdSmartToy aria-hidden="true" />
                    </div>
                  )}
                  <div className={`pa-msg-wrap pa-msg-wrap--${msg.role}`}>
                    <div className={`pa-bubble pa-bubble--${msg.role}`}>
                      {msg.role === 'agent'
                        ? <AgentMessage text={msg.text} />
                        : msg.text}
                    </div>
                    {msg.role === 'agent' && msg.hints && msg.hints.length > 0 && (
                      <div className="pa-hints">
                        {msg.hints.map((hint, i) => {
                          const active = msg.id === lastHintsMsgId && !isLoading;
                          return (
                            <button
                              key={i}
                              className={`pa-hint-chip${active ? '' : ' pa-hint-chip--stale'}`}
                              onClick={() => active && sendMessage(hint)}
                              disabled={!active}
                              aria-label={`Suggest: ${hint}`}
                            >
                              {hint}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className={`pa-meta pa-meta--${msg.role}`}>
                      <span className="pa-time">{msg.timestamp}</span>
                      <button
                        className="pa-copy-btn"
                        onClick={() => copyMessage(msg.id, msg.text)}
                        title="Copy message"
                        aria-label="Copy message"
                      >
                        <MdContentCopy aria-hidden="true" />
                        {copiedId === msg.id ? 'Copied' : ''}
                      </button>
                      {msg.role === 'agent' && msg.jsonData !== undefined && (
                        <button
                          className="pa-json-btn"
                          onClick={() => setViewingJson({ messageId: msg.id, data: msg.jsonData! })}
                          title="View JSON data"
                          aria-label="View JSON data"
                        >
                          <MdCode aria-hidden="true" />
                          View Attachment
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isLoading && (
                <div className="pa-row pa-row--agent">
                  <div className="pa-avatar">
                    <MdSmartToy aria-hidden="true" />
                  </div>
                  <div className="pa-msg-wrap pa-msg-wrap--agent">
                    <div className="pa-bubble pa-bubble--agent pa-bubble--typing">
                      <span className="pa-dot" />
                      <span className="pa-dot" />
                      <span className="pa-dot" />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* ── Input area ── */}
        <div className="pa-input-area">
          <div className="pa-input-inner">
            <textarea
              ref={textareaRef}
              className="pa-textarea"
              placeholder="Ask about a product — e.g. 'Why isn't product P10001 showing up for BRS?' (Shift+Enter for new line)"
              value={inputValue}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <button
              className="pa-send-btn"
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
            >
              <MdSend />
            </button>
          </div>
          <p className="pa-input-hint">
            Shift+Enter for new line · Enter to send
          </p>
        </div>

        {/* ── JSON Viewer Modal ── */}
        {viewingJson !== null && (
          <JsonViewerModal
            data={viewingJson.data}
            onClose={closeJsonModal}
            onDownload={handleJsonDownload}
          />
        )}

      </div>
    </Layout>
  );
};

export default ProductAgent;
